/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useRef, useEffect } from 'react';
import block from 'propmods';
import { useState } from 'react';
import { TextInput, Button } from 'lego-on-react';
import { TextInputProps, TextInputSuggestsMeta } from 'lego-on-react-custom-types';
import { useDebouncedCallback } from 'use-debounce';
import * as keyset from 'i18n/Settings';
import i18n from '@yandex-int/i18n';
import { MarketDevice } from '../../model/devices';
import { ApiError } from '../../api';
import { useFetchingHandler } from '../../hooks/useFetchingHandler';
import { FormError } from '../Form/FormError';
import { DeviceLinkingError, getDeviceLinkingErrorMessage, DeviceLinkingServerError } from '../../utils/market';
import { Loading } from '../Loading/Loading';
import { devicesApi } from '../../api';
import { useCurrentAliceApp } from '../../hooks/useCurrentAliceApp';
import { DeviceLinkingSuggestCard } from '../DeviceLinkingCard/DeviceLinkingSuggestCard';
import { useSettingsPageContext } from '../../context/settingsPageContext';

import './DeviceLinkingInput.scss';

const b = block('DeviceLinkingInput');
const t = i18n(keyset);

interface DeviceLinkingInputProps {
    devices: MarketDevice[];
    onDeviceAdd: (device: MarketDevice) => void;
    disabled?: boolean;
}

const isNumeric = (str: string) => {
    return Boolean(str.match(/^[\d]+$/));
};

const makeDevicesSuggest = (devices: MarketDevice[]) => {
    return devices.map((device, i) => <DeviceLinkingSuggestCard device={device} key={i} />);
};

export const DeviceLinkingInput: React.FC<DeviceLinkingInputProps> = ({ disabled, onDeviceAdd, devices }) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const skill = useCurrentAliceApp()!;
    const [inputText, setInputText] = useState('');
    const [errorCode, setErrorCode] = useState<DeviceLinkingError | undefined>(undefined);
    const [isSuggestDataFetching, setIsSuggestDataFetching] = useState(false);
    const inputTextRef = useRef('');
    const { isDraft } = useSettingsPageContext();

    useEffect(() => {
        inputTextRef.current = inputText;
    }, [inputText]);

    useEffect(() => {
        return () => devicesApi.clearSearchCache();
    }, []);

    const addDeviceHandler = useCallback(
        (deviceId: string) =>
            isDraft
                ? devicesApi.addDevice(skill.id, deviceId, skill.channel)
                : devicesApi.addPublishedDevice(skill.id, deviceId, skill.channel),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );
    const onLoad = useCallback((device) => onDeviceAdd(device), [onDeviceAdd]);
    const onError = useCallback(
        (e) => {
            if (e instanceof ApiError && e.payload) {
                if (e.payload.errorCode === DeviceLinkingServerError.NOT_FOUND) {
                    setErrorCode(DeviceLinkingError.NOT_FOUND);

                    return;
                }

                if (e.payload.errorCode === DeviceLinkingServerError.INVALID_ID) {
                    setErrorCode(DeviceLinkingError.INVALID);

                    return;
                }
            }

            setErrorCode(DeviceLinkingError.UNKNOWN);
        },
        [setErrorCode],
    );

    const [addDeviceStateful, state] = useFetchingHandler({
        handler: addDeviceHandler,
        onLoad,
        onError,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onAdd = async () => {
        if (!inputText) {
            return;
        }

        setErrorCode(undefined);

        if (devices.some((device) => device.marketId === inputText)) {
            setErrorCode(DeviceLinkingError.DUPLICATE);

            return;
        }

        await addDeviceStateful(inputText);

        setInputText('');
    };

    const dataProvider = useDebouncedCallback(async (suggestUrl, val, callback) => {
        if (val === '' || isNumeric(val)) {
            callback([]);

            return;
        }

        setIsSuggestDataFetching(true);

        try {
            const devices = await devicesApi.searchMarketDevices(val);

            if (devices.length === 0 || val !== inputTextRef.current) {
                callback([]);

                return;
            }

            callback(makeDevicesSuggest(devices));
        } finally {
            setIsSuggestDataFetching(false);
        }
    }, 200);

    const onChange = async (value: string | JSX.Element, ...args: any[]) => {
        // Недокументированная функциональность TextInput в lego-on-react
        const [, meta] = args as [TextInputProps, TextInputSuggestsMeta | undefined];

        if (typeof value === 'string') {
            setInputText(value);
            setErrorCode(undefined);

            return;
        }

        if (meta && meta.source === 'suggest' && typeof meta.itemIndex !== 'number') {
            const device: MarketDevice = value.props.device;

            if (devices.some((d) => device.marketId === d.marketId)) {
                setErrorCode(DeviceLinkingError.DUPLICATE);

                return;
            }

            setErrorCode(undefined);

            await addDeviceStateful(device.marketId);

            setInputText('');
        }
    };

    const isNumericInput = isNumeric(inputText);

    const onKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLElement>) => {
            if (e.key === 'Enter' && isNumericInput) {
                e.stopPropagation();
                e.preventDefault();
                void onAdd();
            }
        },
        [onAdd, isNumericInput],
    );

    const clearError = () => setErrorCode(undefined);

    const errorMessage = getDeviceLinkingErrorMessage(errorCode);

    const isLoading = state === 'loading' || isSuggestDataFetching;

    return (
        <div {...b()}>
            <div {...b('input-container')}>
                <div {...b('input')} onClick={clearError}>
                    <TextInput
                        theme="normal"
                        size="s"
                        text={inputText}
                        disabled={disabled}
                        suggest
                        dataprovider={dataProvider as any}
                        onChange={onChange}
                        onKeyDown={onKeyDown}
                    />
                    {isLoading && (
                        <div {...b('loading')}>
                            <Loading size="xxs" />
                        </div>
                    )}
                </div>
                {isNumericInput && (
                    <Button theme="normal" size="s" onClick={onAdd} disabled={disabled || state === 'loading'}>
                        {t('devices-add')}
                    </Button>
                )}
            </div>
            {errorMessage && <FormError offsetTop>{errorMessage}</FormError>}
        </div>
    );
};
